import { Tooltip } from "@material-ui/core";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { LuChevronLeft, LuChevronRight, LuChevronsLeft, LuChevronsRight, LuInfo } from "react-icons/lu";

// Cargo switcher above the port operations: selected cargo is filled, the rest scroll sideways.
const CargoTabs = ({ list = [], selectedId, onSelect = () => { } }) => {
  const trackRef = useRef(null);
  const selectedRef = useRef(null);
  const [collapsed, setCollapsed] = useState(false);
  const [edge, setEdge] = useState({ left: false, right: false });

  const updateEdge = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setEdge({
      left: el.scrollLeft > 0,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  const holdRef = useRef({ raf: 0, timer: 0 });

  const stopScroll = useCallback(() => {
    cancelAnimationFrame(holdRef.current.raf);
    clearTimeout(holdRef.current.timer);
  }, []);

  // click = one jump, hold = keeps scrolling until released
  const startScroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    stopScroll();
    el.scrollLeft += dir * 200;
    holdRef.current.timer = setTimeout(() => {
      const step = () => {
        const before = el.scrollLeft;
        el.scrollLeft += dir * 12;
        if (el.scrollLeft === before) return; // reached the end
        holdRef.current.raf = requestAnimationFrame(step);
      };
      step();
    }, 300);
  };

  useEffect(() => stopScroll, [stopScroll]);

  // bring the selected cargo into view only when the selection changes
  useEffect(() => {
    // scroll only the strip sideways: scrollIntoView would also scroll the whole page down to the tab bar
    const track = trackRef.current;
    const tab = selectedRef.current;
    if (!track || !tab) return;
    const t = track.getBoundingClientRect();
    const b = tab.getBoundingClientRect();
    if (b.left < t.left) track.scrollLeft -= t.left - b.left;
    else if (b.right > t.right) track.scrollLeft += b.right - t.right;
  }, [selectedId, list.length, collapsed]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    // vertical wheel scrolls the strip sideways
    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("resize", updateEdge);
    updateEdge();
    return () => {
      el.removeEventListener("wheel", onWheel);
      window.removeEventListener("resize", updateEdge);
    };
  }, [list.length, updateEdge, collapsed]);

  if (!list.length) return null;

  const arrowClass = (enabled) =>
    clsx(
      "w-5 shrink-0 flex items-center justify-center border-none bg-transparent text-slate-700 transition-colors",
      enabled ? "cursor-pointer hover:text-[#313C4C]" : "opacity-30 cursor-default"
    );

  return (
    <>
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        title={collapsed ? "Show all cargos" : "Hide cargo list"}
        className="shrink-0 flex items-center gap-1 h-[26px] px-1 bg-transparent border-none text-xs font-semibold text-[#313C4C] cursor-pointer select-none"
      >
        Cargo List
        {collapsed ? <LuChevronsRight className="w-3.5 h-3.5" /> : <LuChevronsLeft className="w-3.5 h-3.5" />}
      </button>
      {!collapsed && (
      <>
      <Tooltip
        title={<span className="text-xs">Switch between your cargos here. The selected cargo decides the vessels, port operations and results shown below.</span>}
        arrow
      >
        <span className="shrink-0 flex items-center text-slate-500 hover:text-[#313C4C] cursor-help">
          <LuInfo className="w-3.5 h-3.5" />
        </span>
      </Tooltip>
      <div className="flex flex-1 min-w-0 h-[26px] items-stretch">
      <button type="button" aria-label="Scroll cargos left" disabled={!edge.left} onPointerDown={() => startScroll(-1)} onPointerUp={stopScroll} onPointerLeave={stopScroll} onPointerCancel={stopScroll} className={arrowClass(edge.left)}>
        <LuChevronLeft className="w-3.5 h-3.5" />
      </button>
      <div
        ref={trackRef}
        onScroll={updateEdge}
        className="relative flex flex-1 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {list.map((query) => {
          const selected = selectedId === query._id;
          return (
            <button
              key={query._id}
              type="button"
              ref={selected ? selectedRef : undefined}
              onClick={() => !selected && onSelect(query)}
              title={query.name}
              className={clsx(
                "shrink-0 max-w-[200px] truncate h-full px-2.5 text-xs bg-transparent border-0 border-b-2 border-solid transition-colors select-none",
                selected
                  ? "border-[#313C4C] text-[#313C4C] font-semibold cursor-default"
                  : "border-transparent text-slate-600 font-normal cursor-pointer hover:text-slate-900"
              )}
            >
              {query.name}
            </button>
          );
        })}
      </div>
      <button type="button" aria-label="Scroll cargos right" disabled={!edge.right} onPointerDown={() => startScroll(1)} onPointerUp={stopScroll} onPointerLeave={stopScroll} onPointerCancel={stopScroll} className={arrowClass(edge.right)}>
        <LuChevronRight className="w-3.5 h-3.5" />
      </button>
      </div>
      </>
      )}
    </>
  );
};

export default CargoTabs;
